import {
  collageFacesCut,
  collageRulesOf,
  collageSlotGeoms,
  collageSlotGeomsOn,
  collageStatus,
  describeCollage,
  isCollageAnswer,
  normalizeCollageAnswer,
  relayoutCollage,
  type CollageAnswer,
  type CollageContext,
  type CollageRules,
  type CollageSlotGeom,
  type CollageStatus,
  type Scene,
} from '@pz/scene';
import { createLayer, pointerIsCoarse, type LayerHandle } from '../layers.js';
import { assessResolution } from '../photoquality.js';
import { applyValues, uploadTargetsOf } from '../preview.js';
import type { OptionDef, ProductDefinition, Values } from '../types.js';
import type { CollageBoardHost, CollageBoardOptions, CollageRender, OpenCollageBoard } from './board-types.js';
import type { CollageBoardHostExtras } from './board.js';
import type { CollageIntakeHost, CollageMedia } from './intake.js';

/**
 * One collage question's side of the step ↔ board contract (docs/photo-
 * collage.md §7.1): the answer as the form holds it, the print side it is
 * judged on, the undo stack both the step's toast and the board share, and
 * where every photo's pixels are.
 *
 * Every change of the answer is a pure `@pz/scene` command made by the caller
 * and handed to `commit` (undoable) — or, for a photo that just landed, to
 * `land` (not undoable, and carried into every undo snapshot, so "Poništi"
 * never takes an upload back).
 */

/** How deep "Poništi" goes. */
export const COLLAGE_UNDO_DEPTH = 30;

export interface CollageHostDeps {
  option: OptionDef;
  product: ProductDefinition;
  /** The form's answers now (condition-hidden ones left out). */
  values(): Values;
  /** Bumped whenever an answer changes: the active slots are cached per version. */
  version(): number;
  read(): CollageAnswer | null;
  /** Writes the question's answer (null = no answer). */
  store(next: CollageAnswer | null): void;
  /** Where layers and toasts go: the widget's shadow root. */
  layerHost(): ShadowRoot | HTMLElement;
  /** The page's own way to resolve the print side (main.ts); the default is the same applyValues path. */
  printScene?(values: Values): Scene | undefined;
  /**
   * The page's own way to resolve EVERY print side holding the collage's
   * slots, in side order (a box printed on two sheets). Wins over
   * `printScene`; the default is `collagePrintScenes`.
   */
  printScenes?(values: Values): Scene[];
  /** Preview.renderView, where the page has one. */
  render?(scene: Scene, opts: { scale: number; viewport?: { x: number; y: number; w: number; h: number } }): Promise<CollageRender | undefined>;
  /** Where an asset's pixels are when this question did not upload it (a restored order). */
  resolveUrl?(assetId: string): string | undefined;
  /** A collage photo landed: the preview learns its draw copy. Nothing else happens to it. */
  onAsset?(media: CollageMedia): void;
  /** Opens the picker (the step owns the file input). */
  pickPhotos(target?: { elementId: string }): void;
  /** The board opened or closed (the page pauses its own preview meanwhile). */
  onBoard?(open: boolean): void;
  /** Says a sentence to a screen reader. */
  announce?(text: string): void;
  /** The layout variant changed and photos moved to "Rezerva". */
  onRelayout?(): void;
  pointer?(): 'touch' | 'mouse';
  /** For tests: the board to open instead of `./board.js`. */
  openBoard?: OpenCollageBoard;
}

/** The print side that holds a collage's slots: the non-preview side with the most of them. */
export function collagePrintSide(product: Pick<ProductDefinition, 'sides'>, targets: readonly string[]): Scene | undefined {
  const wanted = new Set(targets);
  let best: { side: Scene; count: number } | undefined;
  for (const side of product.sides ?? []) {
    if (side.target === 'preview') continue;
    const count = (side.elements ?? []).filter((el) => wanted.has(el.id)).length;
    if (count > 0 && (!best || count > best.count)) best = { side, count };
  }
  return best?.side;
}

/** Option value id → its stored text, as `applyValues` matches layout choices. */
function valueLabelsOf(product: Pick<ProductDefinition, 'options'>): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const option of product.options ?? []) for (const value of option.values ?? []) labels[value.id] = value.value;
  return labels;
}

/**
 * Every print side that holds any of the collage's slots, in the template's
 * side order (mockups — target 'preview' — never count): a box printed on two
 * A3 sheets has two, each its own print file. `index` is the side's place in
 * `product.sides` (side ids are not guaranteed unique; element ids are).
 */
export function collagePrintSides(
  product: Pick<ProductDefinition, 'sides'>,
  targets: readonly string[],
): Array<{ side: Scene; index: number }> {
  const wanted = new Set(targets);
  const out: Array<{ side: Scene; index: number }> = [];
  (product.sides ?? []).forEach((side, index) => {
    if (side.target === 'preview') return;
    if ((side.elements ?? []).some((el) => wanted.has(el.id))) out.push({ side, index });
  });
  return out;
}

/**
 * Every print side holding the collage's slots (`collagePrintSides`), each
 * with every answer applied — the preview's own resolution path
 * (`applyValues`), so the slots that count are exactly the ones the preview
 * draws and the server's commit judges, on every sheet.
 */
export function collagePrintScenes(product: ProductDefinition, optionId: string, values: Values): Scene[] {
  const option = product.options.find((o) => o.id === optionId);
  if (!option) return [];
  const sides = collagePrintSides(product, collageRulesOf(option.config).targetElementIds);
  if (sides.length === 0) return [];
  const labels = valueLabelsOf(product);
  const targets = uploadTargetsOf(product.options);
  return sides.map(({ side }) => applyValues(side, product.bindings, values, labels, product.libraries ?? {}, targets));
}

/**
 * The print side holding the collage's slots with every answer applied — the
 * one with the most of them when the collage spans several sheets
 * (`collagePrintScenes` gives them all).
 */
export function collagePrintScene(product: ProductDefinition, optionId: string, values: Values): Scene | undefined {
  const option = product.options.find((o) => o.id === optionId);
  if (!option) return undefined;
  const side = collagePrintSide(product, collageRulesOf(option.config).targetElementIds);
  if (!side) return undefined;
  return applyValues(side, product.bindings, values, valueLabelsOf(product), product.libraries ?? {}, uploadTargetsOf(product.options));
}

/**
 * The active slots over one or several resolved print sheets. One sheet reads
 * exactly as before (`collageSlotGeoms`); several go through
 * `collageSlotGeomsOn` (printed-area ranks, each slot tagged with its sheet),
 * with the sheets' ids made unique first — Studio-made sides may share one.
 */
export function collageSlotsOnSheets(scenes: readonly Scene[], targets: readonly string[]): CollageSlotGeom[] {
  if (scenes.length === 0) return [];
  if (scenes.length === 1) return collageSlotGeoms(scenes[0]!, targets);
  const seen = new Set<string>();
  const unique = scenes.map((scene, i) => {
    let id = scene.id || `sheet-${i + 1}`;
    if (seen.has(id)) id = `${id}#${i + 1}`;
    seen.add(id);
    return id === scene.id ? scene : { ...scene, id };
  });
  return collageSlotGeomsOn(unique, targets);
}

/** "24 slike": the filled ACTIVE slots, the cart line's and the summary's words. */
export function collageSummary(answer: unknown, activeIds: readonly string[] | undefined): string {
  return isCollageAnswer(answer) ? describeCollage(answer, activeIds) : '';
}

/**
 * The topmost element of `scene` under the scene point (x, y) that `accept`
 * takes — a collage slot under a tap on the page's preview. Turned slots are
 * hit by their turned box; hidden elements are skipped.
 */
export function collageSlotAtPoint(
  scene: Pick<Scene, 'elements'>,
  x: number,
  y: number,
  accept: (elementId: string) => boolean,
): string | undefined {
  let best: { id: string; z: number } | undefined;
  scene.elements.forEach((el, order) => {
    if (el.visible === false || !el.transform || !accept(el.id)) return;
    const { cx, cy, w, h } = el.transform;
    const turn = ((el.transform.rotation ?? 0) * Math.PI) / 180;
    const dx = x - cx;
    const dy = y - cy;
    // Into the element's own axes.
    const u = dx * Math.cos(-turn) - dy * Math.sin(-turn);
    const v = dx * Math.sin(-turn) + dy * Math.cos(-turn);
    if (Math.abs(u) > Math.abs(w) / 2 || Math.abs(v) > Math.abs(h) / 2) return;
    const z = (el.z ?? 0) + order / 1e6;
    if (!best || z >= best.z) best = { id: el.id, z };
  });
  return best?.id;
}

async function loadBoard(): Promise<OpenCollageBoard | undefined> {
  try {
    const board = await import('./board.js');
    return typeof board.openCollageBoard === 'function' ? board.openCollageBoard : undefined;
  } catch {
    return undefined;
  }
}

export class CollageHost implements CollageBoardHost, CollageBoardHostExtras, CollageIntakeHost {
  readonly label: string;
  readonly rules: CollageRules;
  readonly optionId: string;
  private readonly stack: Array<CollageAnswer | null> = [];
  private touchCount = 0;
  private cache: { version: number; slots: CollageSlotGeom[] } | undefined;
  private sheetCache: { version: number; scenes: Scene[] } | undefined;
  private readonly thumbs = new Map<string, string>();
  private readonly draws = new Map<string, string>();
  private readonly framers = new Map<string, string>();
  private boardOpen = false;
  /** The active slot ids last seen, to notice a layout switch. */
  private lastActive: string | undefined;
  /** The open board's ears for what became of photos picked from it (see `notice`). */
  private readonly noticeListeners = new Set<(text: string) => void>();

  constructor(private readonly deps: CollageHostDeps) {
    this.optionId = deps.option.id;
    this.label = deps.option.label?.trim() || 'Tvoje slike';
    this.rules = collageRulesOf(deps.option.config);
  }

  // ── The board's host ───────────────────────────────────────────────────

  get pointer(): 'touch' | 'mouse' {
    return this.deps.pointer?.() ?? (pointerIsCoarse() ? 'touch' : 'mouse');
  }

  openLayer(className: string, onClose: () => void): { root: HTMLElement; close(): void } {
    const root = document.createElement('div');
    root.className = `${className}-root`;
    let closed = false;
    let layer: LayerHandle | undefined;
    layer = createLayer({
      host: this.deps.layerHost(),
      className,
      content: root,
      label: this.label,
      closeOnBackdrop: false,
      onClose: () => {
        if (closed) return;
        closed = true;
        try {
          onClose();
        } finally {
          // Gone from the page once closed, however it closed (Escape too).
          setTimeout(() => layer?.destroy(), 0);
        }
      },
    });
    layer.open();
    return { root, close: () => layer?.close() };
  }

  /**
   * The print side with every current answer applied — fresh on each call.
   * With several sheets, the one holding the most active slots (sheet 0 on a
   * tie); `printScenes` gives them all.
   */
  printScene(): Scene {
    const scenes = this.scenes();
    if (scenes.length > 1) {
      const counts = scenes.map(() => 0);
      for (const slot of this.activeSlots()) {
        const at = this.sheetOf(slot.elementId);
        if (at >= 0) counts[at]!++;
      }
      let best = 0;
      counts.forEach((n, i) => {
        if (n > counts[best]!) best = i;
      });
      return scenes[best]!;
    }
    const scene = scenes[0];
    if (scene) return scene;
    // No print side holds the slots (a broken template): an empty page, so the
    // board has nothing to show rather than throwing.
    return { schemaVersion: 1, id: 'none', name: '', target: 'print', width: 1, height: 1, dpi: 300, elements: [] } as unknown as Scene;
  }

  /**
   * Every print sheet holding the collage's slots, in side order, with every
   * current answer applied (each its own print file). Fresh per answer.
   */
  printScenes(): Scene[] {
    return this.scenes();
  }

  /** Which sheet (0-based, `printScenes` order) holds the element; -1 when none does. */
  sheetOf(elementId: string): number {
    return this.scenes().findIndex((scene) => scene.elements.some((el) => el.id === elementId));
  }

  /** A sheet's name for the shopper: the side's own name, else "List 2". */
  sheetName(index: number): string {
    return this.scenes()[index]?.name?.trim() || `List ${index + 1}`;
  }

  private scenes(): Scene[] {
    const version = this.deps.version();
    if (this.sheetCache?.version === version) return this.sheetCache.scenes;
    const values = this.deps.values();
    let scenes: Scene[];
    if (this.deps.printScenes) scenes = this.deps.printScenes(values);
    else if (this.deps.printScene) {
      const one = this.deps.printScene(values);
      scenes = one ? [one] : [];
    } else scenes = collagePrintScenes(this.deps.product, this.optionId, values);
    this.sheetCache = { version, scenes };
    return scenes;
  }

  /** Active (visible) slots over every sheet, in target order; cached until an answer changes. */
  activeSlots(): CollageSlotGeom[] {
    const version = this.deps.version();
    if (this.cache?.version === version) return this.cache.slots;
    const slots = collageSlotsOnSheets(this.scenes(), this.rules.targetElementIds);
    this.cache = { version, slots };
    return slots;
  }

  activeIds(): string[] {
    return this.activeSlots().map((s) => s.elementId);
  }

  answer(): CollageAnswer | null {
    return this.deps.read();
  }

  context(): CollageContext {
    return { slots: this.activeSlots(), rules: this.rules };
  }

  status(): CollageStatus {
    return collageStatus(this.answer(), this.activeSlots(), this.rules);
  }

  commit(next: CollageAnswer, announce?: string): void {
    const current = this.answer();
    if (next === current) return;
    this.stack.push(current);
    while (this.stack.length > COLLAGE_UNDO_DEPTH) this.stack.shift();
    this.touchCount++;
    this.deps.store(next);
    if (announce) this.deps.announce?.(announce);
  }

  undo(): boolean {
    if (this.stack.length === 0) return false;
    const previous = this.stack.pop()!;
    this.touchCount++;
    this.deps.store(previous);
    return true;
  }

  canUndo(): boolean {
    return this.stack.length > 0;
  }

  async render(
    scene: Scene,
    opts: { scale: number; viewport?: { x: number; y: number; w: number; h: number } },
  ): Promise<CollageRender | undefined> {
    try {
      return (await this.deps.render?.(scene, opts)) ?? undefined;
    } catch {
      return undefined;
    }
  }

  thumbUrl(assetId: string): string | undefined {
    return this.thumbs.get(assetId) ?? this.drawUrl(assetId);
  }

  drawUrl(assetId: string): string | undefined {
    return this.draws.get(assetId) ?? this.deps.resolveUrl?.(assetId);
  }

  framerUrl(assetId: string): string | undefined {
    return this.framers.get(assetId) ?? this.drawUrl(assetId);
  }

  pickPhotos(target?: { elementId: string }): void {
    this.deps.pickPhotos(target);
  }

  slotAdvice(elementId: string): { facesCut: boolean; soft: boolean; repeat: number } {
    const none = { facesCut: false, soft: false, repeat: 0 };
    const answer = this.answer();
    const slots = this.activeSlots();
    const slot = slots.find((s) => s.elementId === elementId);
    const fill = answer?.slots[elementId];
    if (!answer || !slot || !fill) return none;
    const photo = answer.photos.find((p) => p.assetId === fill.assetId);
    const facesCut = Boolean(
      photo && !fill.manual && collageFacesCut(photo, slot, fill.crop, { faceFraming: this.rules.faceFraming }) > 0,
    );
    const zoom = fill.crop?.zoom ?? 1;
    const soft = Boolean(
      photo?.w && photo.h &&
        assessResolution({ w: photo.w, h: photo.h }, { w: slot.w * zoom, h: slot.h * zoom, dpi: slot.dpi }).verdict === 'warn',
    );
    const uses = slots.filter((s) => answer.slots[s.elementId]?.assetId === fill.assetId).length;
    return { facesCut, soft, repeat: uses > 1 ? uses : 0 };
  }

  // ── The intake's host ──────────────────────────────────────────────────

  land(apply: (answer: CollageAnswer | null) => CollageAnswer | null): void {
    for (let i = 0; i < this.stack.length; i++) this.stack[i] = apply(this.stack[i] ?? null);
    const current = this.answer();
    const next = apply(current);
    if (next !== current) this.deps.store(next);
  }

  settle(from: CollageAnswer, next: CollageAnswer): void {
    if (this.answer() !== from) return;
    this.deps.store(next);
  }

  touches(): number {
    return this.touchCount;
  }

  media(media: CollageMedia): void {
    this.draws.set(media.assetId, media.drawUrl);
    this.thumbs.set(media.assetId, media.thumbUrl);
    this.framers.set(media.assetId, media.framerUrl);
    this.deps.onAsset?.(media);
  }

  // ── The form's side ────────────────────────────────────────────────────

  /** Thumbnails and framer copies, for the draft. */
  mediaMaps(): { thumbs: Record<string, string>; framers: Record<string, string> } {
    const inAnswer = new Set((this.answer()?.photos ?? []).map((p) => p.assetId));
    const pick = (map: Map<string, string>): Record<string, string> =>
      Object.fromEntries([...map].filter(([id]) => inAnswer.has(id)));
    return { thumbs: pick(this.thumbs), framers: pick(this.framers) };
  }

  /** URLs from a draft or a restored order (`collageThumbs`). */
  restoreMedia(media: { thumbs?: Record<string, string>; framers?: Record<string, string>; draws?: Record<string, string> }): void {
    const put = (map: Map<string, string>, from: Record<string, string> | undefined): void => {
      for (const [id, url] of Object.entries(from ?? {})) if (typeof url === 'string' && /^(https?:|blob:|data:|\/)/.test(url)) map.set(id, url);
    };
    put(this.thumbs, media.thumbs);
    put(this.framers, media.framers);
    put(this.draws, media.draws);
  }

  /** A restored or reset answer: the undo stack belonged to the old one. */
  restored(): void {
    this.stack.length = 0;
    this.lastActive = undefined;
  }

  /** Normalises whatever a draft or an order brought back, by this question's rules. */
  normalize(value: unknown): CollageAnswer | null {
    return normalizeCollageAnswer(value, this.rules).answer;
  }

  /**
   * Another answer changed. When it switched the layout ("15 slika" ↔ "24
   * slike"), the photos of slots that went away move into the new empty
   * slots and the rest wait in "Rezerva" (`relayoutCollage`). True when the
   * active slots changed (the step has something new to say), whether or not
   * there were photos to move.
   */
  afterAnswersChanged(): boolean {
    const key = this.activeIds().join('|');
    const before = this.lastActive;
    this.lastActive = key;
    if (before === undefined || before === key) return false;
    const answer = this.answer();
    if (!answer || answer.photos.length === 0) return true;
    const next = relayoutCollage(answer, this.context());
    // Snapshots from the other layout would bring back its empty slots.
    this.stack.length = 0;
    this.touchCount++;
    if (next !== answer) this.deps.store(next);
    this.deps.onRelayout?.();
    return true;
  }

  get isBoardOpen(): boolean {
    return this.boardOpen;
  }

  /** The board listens while it is open (board.ts `CollageBoardHostExtras`); returns the unsubscribe. */
  onNotice(listener: (text: string) => void): () => void {
    this.noticeListeners.add(listener);
    return () => {
      this.noticeListeners.delete(listener);
    };
  }

  /**
   * Something about photos picked from the board ("Već imaš sve slike koje
   * staju…", "1 slika nije poslata."): said on the board when it is open.
   * False when no board heard it (the step shows it anyway).
   */
  notice(text: string): boolean {
    if (!text || !this.boardOpen || this.noticeListeners.size === 0) return false;
    for (const listener of [...this.noticeListeners]) listener(text);
    return true;
  }

  /** "Raspored slika", focused on one slot when given. Resolves when it closes. */
  async openBoard(focus?: string): Promise<void> {
    if (this.boardOpen) return;
    this.boardOpen = true;
    this.deps.onBoard?.(true);
    try {
      const open = this.deps.openBoard ?? (await loadBoard());
      if (!open) return;
      const opts: CollageBoardOptions = focus && this.activeIds().includes(focus) ? { focus } : {};
      await open(this, opts);
    } finally {
      this.boardOpen = false;
      this.deps.onBoard?.(false);
    }
  }
}
